/**
 * App Component
 * Entry point for the React Template application
 */
import React, { Suspense } from "react";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import I18nProvider from "./Providers/i18nProvider";
// Core providers and configuration
import { cacheRtl, cacheLtr } from "./theme/rtl.js";
import { createAppTheme } from "./theme/theme.js";
import { ErrorBoundary } from "./components/index.js";
import ErrorFallback from "./components/ui/errors/ErrorFallback.jsx";
import { AppRouter } from "./routes/index.js";
import { LazyToastContainer } from "./utils/toast/toast.js";
import { useLanguageDirection } from "./hooks/index.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { arSA, enUS } from "date-fns/locale";



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const { dir } = useLanguageDirection();
  React.useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
  }, [dir]);
  const locale = dir === "rtl" ? arSA : enUS;
  return (
    <>
      <Suspense fallback={null}>
        <LazyToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar
          rtl={dir === "rtl"}
        />
      </Suspense>
      <ErrorBoundary fallback={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <I18nProvider>
            <CacheProvider value={dir === "rtl" ? cacheRtl : cacheLtr}>
              <ThemeProvider theme={createAppTheme(dir)}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={locale}
                >
                  <CssBaseline />
                  <AppRouter />
                </LocalizationProvider>
              </ThemeProvider>
            </CacheProvider>
          </I18nProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;
